import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api';
import DashboardLayout from '../../../Components/Dashboard/DashboardLayout';
import './AdminSuite.css';

const emptyIntake = {
  name: '',
  start_date: '',
  end_date: '',
  capacity: '',
  status: 'open',
  payment_instruction: '',
};

const AdminCreateCoursePage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    category: '',
    description: '',
    trainer: '',
    duration: '',
    schedule: '',
    fee: '',
    fee_currency: 'RWF',
    status: 'draft',
  });
  const [intakes, setIntakes] = useState([{ ...emptyIntake }]);
  const [saving, setSaving] = useState(false);

  const setIntakeField = (index, key, value) => {
    setIntakes(prev => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  };

  const addIntake = () => {
    setIntakes(prev => [...prev, { ...emptyIntake }]);
  };

  const removeIntake = index => {
    setIntakes(prev => prev.filter((_, i) => i !== index));
  };

  const submit = async e => {
    e.preventDefault();

    const cleanedIntakes = intakes
      .filter(i => i.name.trim())
      .map(i => ({
        name: i.name.trim(),
        start_date: i.start_date || null,
        end_date: i.end_date || null,
        capacity: i.capacity ? Number(i.capacity) : null,
        status: i.status || 'open',
        payment_instruction: i.payment_instruction?.trim() || null,
      }));

    for (const intake of cleanedIntakes) {
      // Check if from date is in future
      if (intake.start_date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startDate = new Date(intake.start_date);
        startDate.setHours(0, 0, 0, 0);
        if (startDate <= today) {
          toast.error(`Intake "${intake.name}": start date must be in the future`);
          return;
        }
      }
      // Check if from date is less than to date
      if (intake.start_date && intake.end_date && intake.end_date < intake.start_date) {
        toast.error(`Intake "${intake.name}": end date cannot be before start date`);
        return;
      }
    }

    const payload = {
      ...form,
      fee: Number(form.fee || 0),
      intakes: cleanedIntakes,
    };

    try {
      setSaving(true);
      await api.post('/admin/courses', payload);
      toast.success('Course created successfully');
      navigate('/admin/courses');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create course');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container py-4 admin-suite">
        <div className="admin-suite-header">
          <div>
            <h3 className="admin-suite-title">Create New Course</h3>
            <p className="admin-suite-subtitle">Set course details, currency and intake date ranges.</p>
          </div>
        </div>

        <form onSubmit={submit} className="row g-3">
          <div className="col-12">
            <div className="card admin-card border-0 shadow-sm">
              <div className="card-body p-4">
                <h5 className="admin-section-title">Course information</h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Title</label>
                    <input
                      className="form-control"
                      required
                      placeholder="e.g. IFRS Professional"
                      value={form.title}
                      onChange={e => setForm({ ...form, title: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Category</label>
                    <input
                      className="form-control"
                      required
                      placeholder="Accounting"
                      value={form.category}
                      onChange={e => setForm({ ...form, category: e.target.value })}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      required
                      rows={4}
                      value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Trainer</label>
                    <input
                      className="form-control"
                      required
                      value={form.trainer}
                      onChange={e => setForm({ ...form, trainer: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Duration</label>
                    <input
                      className="form-control"
                      placeholder="e.g. 8 weeks"
                      value={form.duration}
                      onChange={e => setForm({ ...form, duration: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Schedule</label>
                    <input
                      className="form-control"
                      placeholder="e.g. Mon/Wed 6PM"
                      value={form.schedule}
                      onChange={e => setForm({ ...form, schedule: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Fee</label>
                    <input
                      className="form-control"
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      value={form.fee}
                      onChange={e => setForm({ ...form, fee: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Currency</label>
                    <select
                      className="form-select"
                      value={form.fee_currency}
                      onChange={e => setForm({ ...form, fee_currency: e.target.value })}
                    >
                      <option value="RWF">RWF</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="KES">KES</option>
                      <option value="UGX">UGX</option>
                      <option value="TZS">TZS</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={form.status}
                      onChange={e => setForm({ ...form, status: e.target.value })}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card admin-card border-0 shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="admin-section-title mb-0">Course intakes / sessions</h5>
                  <button className="btn btn-outline-primary btn-sm" type="button" onClick={addIntake}>
                    + Add intake
                  </button>
                </div>

                {intakes.map((intake, idx) => (
                  <div key={`intake-${idx}`} className="admin-intake-card rounded-3 p-3 mb-3">
                    <div className="row g-2 align-items-end">
                      <div className="col-md-4">
                        <label className="form-label">Intake name</label>
                        <input
                          className="form-control"
                          placeholder="May 2026 - Evening"
                          value={intake.name}
                          onChange={e => setIntakeField(idx, 'name', e.target.value)}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">From</label>
                        <input
                          type="date"
                          className="form-control"
                          value={intake.start_date}
                          onChange={e => setIntakeField(idx, 'start_date', e.target.value)}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">To</label>
                        <input
                          type="date"
                          className="form-control"
                          value={intake.end_date}
                          onChange={e => setIntakeField(idx, 'end_date', e.target.value)}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Capacity</label>
                        <input
                          className="form-control"
                          type="number"
                          min="1"
                          value={intake.capacity}
                          onChange={e => setIntakeField(idx, 'capacity', e.target.value)}
                        />
                      </div>
                      <div className="col-md-1">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={intake.status}
                          onChange={e => setIntakeField(idx, 'status', e.target.value)}
                        >
                          <option value="open">Open</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                      <div className="col-md-1 d-grid">
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          disabled={intakes.length === 1}
                          onClick={() => removeIntake(idx)}
                          title="Remove intake"
                        >
                          ×
                        </button>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Payment instruction</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          placeholder="e.g. Pay full amount upon registration, or 50% deposit = RWF 88,500. Call 0788393784 for assistance."
                          value={intake.payment_instruction || ''}
                          onChange={e => setIntakeField(idx, 'payment_instruction', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-12">
            <button className="btn btn-primary px-4" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Create Course'}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default AdminCreateCoursePage;
